import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js'

const UserPoolId = process.env.REACT_APP_COGNITO_USERPOOL_ID
const ClientId = process.env.REACT_APP_COGNITO_APP_CLIENT_ID

export const userPool = new CognitoUserPool({ UserPoolId, ClientId })

export const isUserLoggedIn = async () => {
  try {
    await getCurrentUserSession()
    return true
  } catch (error) {
    return false
  }
}

export const authenticateUser = async (username: string, password: string) => {
  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  })

  const userCognito = new CognitoUser({
    Username: username,
    Pool: userPool,
  })

  return new Promise((resolve, reject) => {
    userCognito.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        resolve({
          accessToken: result.getAccessToken().getJwtToken(),
          refreshToken: result.getRefreshToken().getToken(),
        })
      },
      onFailure: reject,
    })
  })
}

export const getCurrentUserSession = async () => {
  return await new Promise<CognitoUserSession | undefined>(
    (resolve, reject) => {
      const user = userPool.getCurrentUser()

      if (!user) {
        return reject(new Error('No user'))
      }

      user.getSession((err: Error | null, session: CognitoUserSession) => {
        if (err) {
          return reject(err)
        }
        if (!session.isValid()) {
          return reject('Session Expired')
        }
        return resolve(session)
      })
    },
  )
}

export const getUserToken = async () => {
  const session = await getCurrentUserSession()

  return session?.getIdToken().getJwtToken()
}

export const logoutUser = async () => {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser()

    if (!user) {
      return reject(new Error('No user'))
    }

    user.signOut(() => {
      resolve(true)
    })
  })
}

export const authFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit,
) => {
  const token = await getUserToken()
  return await fetch(input, {
    ...init,
    headers: new Headers({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json; charset=utf-8',
    }),
  })
}

export const getCurrentUsername = () => {
  const user = userPool.getCurrentUser()

  if (user) {
    return user.getUsername()
  } else {
    throw new Error('No user currently authenticated')
  }
}

export const refreshSession = async () => {
  const user = userPool.getCurrentUser()

  if (user) {
    return new Promise<CognitoUserSession | undefined>((resolve, reject) => {
      user.getSession((err: Error | null, session: CognitoUserSession) => {
        if (err || !session.isValid()) {
          user.refreshSession(
            session.getRefreshToken() as CognitoRefreshToken,
            (err: Error | null, session: CognitoUserSession) => {
              if (err || !session.isValid()) {
                reject(err ? err : new Error('Could not refresh session'))
              } else {
                resolve(session)
              }
            },
          )
        } else {
          resolve(session)
        }
      })
    })
  } else {
    throw new Error('No user currently authenticated')
  }
}

export const changePassword = async (
  oldPassword: string,
  newPassword: string,
) => {
  return new Promise((resolve, reject) => {
    refreshSession()
      .then(async () => {
        const user = userPool.getCurrentUser()

        if (!user) {
          return reject(new Error('No user currently authenticated'))
        }

        user.changePassword(
          oldPassword,
          newPassword,
          (err: Error | undefined, result: 'SUCCESS' | undefined) => {
            if (err) {
              return reject(err)
            } else if (result === 'SUCCESS') {
              return resolve(result)
            } else {
              return reject(new Error('Unexpected result'))
            }
          },
        )
      })
      .catch((err: Error) => {
        return reject(err)
      })
  })
}

export const resetPassword = async (username: string) => {
  const userCognito = new CognitoUser({
    Username: username,
    Pool: userPool,
  })

  return new Promise((resolve, reject) => {
    userCognito.forgotPassword({
      onFailure(err) {
        reject(err)
      },
      onSuccess(data) {
        resolve(data)
      },
    })
  })
}

export const confirmPasswordReset = async (
  username: string,
  newPassword: string,
  verificationCode: string,
) => {
  const userCognito = new CognitoUser({
    Username: username,
    Pool: userPool,
  })

  return new Promise((resolve, reject) => {
    userCognito.confirmPassword(verificationCode, newPassword, {
      onFailure(err) {
        reject(err)
      },
      onSuccess(data) {
        resolve(data)
      },
    })
  })
}
